import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getStockUnitDetails } from '../api/dms/stockUnit.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { IStockUnitDetails } from '../models/dms/stockUnit.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { AttachmentAction, AttachmentAssetType, IAttachment, IAttachmentContainer } from '../models/dms/attachment.model';
import { setSelectedVehicleAttachmentsStoreAsync } from './vehicleAttachmentSlice';
import { RootState } from '.';

interface IStockUnitState {
  selectedStockUnit?: IStockUnitDetails;
  selectedStockUnitLoading: boolean;
}

const initialState: IStockUnitState = {
  selectedStockUnit: undefined,
  selectedStockUnitLoading: false,
};

/// THUNKS

export const setSelectedStockUnitStoreAsync = createAsyncThunk<{}, IStockUnitDetails>('stockUnit/setSelectedStockUnitStoreAsync', async (stockUnit, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected stock unit '${stockUnit.id}' to storage`, '[stockUnit] setSelectedStockUnitStoreAsync');
  try {
    dispatch(setSelectedStockUnitState(stockUnit));
    await updateConfigObject<IStockUnitDetails>(ConfigName.SelectedStockUnit, stockUnit, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchStockUnitDetailsStoreAsync = createAsyncThunk<{}, { stockNumber: number; repairOrderNumber: number }>(
  'stockUnit/fetchStockUnitDetailsByRegistrationNumberStoreAsync',
  async ({ stockNumber, repairOrderNumber }, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Fetching stock unit details from database by stock number '${stockNumber}'`, '[stockUnit] fetchStockUnitDetailsByRegistrationNumberStoreAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setSelectedStockUnitLoadingState(true));

      let attachmentContainer = {
        assetType: AttachmentAssetType.StockUnit,
        key: stockNumber.toString(),
        jobCode: '',
        jobType: '',
        repairOrderNumber: repairOrderNumber.toString(),
        mediaRef: '',
        attachments: [] as IAttachment[],
      } as IAttachmentContainer;

      await dispatch(setSelectedVehicleAttachmentsStoreAsync(attachmentContainer));

      const responseData = await getStockUnitDetails(stockNumber);
      await dispatch(setSelectedStockUnitStoreAsync(responseData));

      // Be sure to include existing unsync attachments
      const { selectedVehicleAttachments } = (getState() as RootState).vehicleAttachments;
      const outstandingAttachments = selectedVehicleAttachments?.attachments.filter((a) => a.action === AttachmentAction.Add) || [];

      // Set the vehicle attachments
      attachmentContainer = {
        ...attachmentContainer,
        attachments: [...responseData.imageAttachments.map((a) => ({ ...a, assetType: AttachmentAssetType.StockUnit })), ...outstandingAttachments] as IAttachment[],
      } as IAttachmentContainer;

      await dispatch(setSelectedVehicleAttachmentsStoreAsync(attachmentContainer));

      dispatch(setIsLoadingCountState(-1));
      dispatch(setSelectedStockUnitLoadingState(false));

      return responseData;
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setSelectedStockUnitLoadingState(false));
    }
  },
);

/// SLICE

export const stockUnitSlice = createSlice({
  name: 'stockUnit',
  initialState,
  reducers: {
    setSelectedStockUnitState: (state, action: PayloadAction<IStockUnitDetails>) => {
      logConsole(LogType.Info, '[stockUnit] setSelectedStockUnitState');
      state.selectedStockUnit = action.payload;
    },
    setSelectedStockUnitLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[stockUnit] setSelectedStockUnitLoadingState: ${action.payload}`);
      state.selectedStockUnitLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setSelectedStockUnitState, setSelectedStockUnitLoadingState } = stockUnitSlice.actions;

/// CUSTOM SELECTORS

export default stockUnitSlice.reducer;
